import {Button, IconButton} from '@wandb/components';
import React, {type ReactElement} from 'react';

import {useWBAgentController} from '../../agentChat/controller';
import {useAgentChatReadonly} from '../../agentChat/useAgentChatReadonly';
import {isInIframe} from '../../util/window';
import {Tooltip} from '../../weave/components/Tooltip';
import {AGENT_NAME} from './constants';

type EntryPointProps = {
  'data-dd-action-name': string;
  'data-test'?: string;
  isDisabled?: boolean;
  onChatStarted?: () => void;
  prompt: string;
  submit: boolean;
};

type WBAgentEntryPointButtonProps = EntryPointProps & {
  label?: string;
};

export const WBAgentEntryPointTooltip = ({
  children,
  label,
  side = 'left',
}: {
  children: ReactElement<React.HTMLAttributes<HTMLElement>, string>;
  label: string;
  side?: 'top' | 'right' | 'bottom' | 'left';
}) => <Tooltip content={label} noTriggerWrap side={side} trigger={children} />;

export const WBAgentEntryPointButton = ({
  'data-dd-action-name': ddActionName,
  'data-test': dataTest,
  isDisabled,
  label,
  onChatStarted,
  prompt,
  submit,
}: WBAgentEntryPointButtonProps) => {
  const {isAvailable, startChat} = useWBAgentController();
  const isReadonly = useAgentChatReadonly();

  return isAvailable && !isInIframe() ? (
    <Button
      data-dd-action-name={ddActionName}
      data-test={dataTest}
      isDisabled={isDisabled || isReadonly}
      startIcon="lightboard-solid"
      variant="secondary"
      onPress={() => {
        const result = startChat(prompt, {submit});
        if (result.ok) {
          onChatStarted?.();
        }
      }}>
      {label ?? `Ask ${AGENT_NAME}`}
    </Button>
  ) : null;
};

type WBAgentEntryPointIconButtonProps = EntryPointProps & {
  'aria-label': string;
};

export const WBAgentEntryPointIconButton = ({
  'aria-label': ariaLabel,
  'data-dd-action-name': ddActionName,
  'data-test': dataTest,
  isDisabled,
  onChatStarted,
  prompt,
  submit,
}: WBAgentEntryPointIconButtonProps) => {
  const {isAvailable, startChat} = useWBAgentController();
  const isReadonly = useAgentChatReadonly();

  return isAvailable && !isInIframe() ? (
    <Tooltip
      content={ariaLabel}
      side="top"
      trigger={
        <IconButton
          aria-label={ariaLabel}
          data-dd-action-name={ddActionName}
          data-test={dataTest}
          icon="lightboard-solid"
          isDisabled={isDisabled || isReadonly}
          variant="secondary"
          onPress={() => {
            const result = startChat(prompt, {submit});
            if (result.ok) {
              onChatStarted?.();
            }
          }}
        />
      }
    />
  ) : null;
};
